import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

export interface UserNote {
  id: string
  content: string
  created_at: string
  updated_at: string
  created_by_name: string | null
}

interface CreateUserNoteParams {
  userId: string
  content: string
}

interface UpdateUserNoteParams {
  userId: string
  noteId: string
  content: string
}

interface DeleteUserNoteParams {
  userId: string
  noteId: string
}

interface ToggleContactedParams {
  userId: string
  wasContacted?: boolean | null
}

export const userNotesQueryKey = (userId: string) => ['user-notes', userId]

export function useUserNotes(userId?: string, search?: string) {
  async function getUserNotes() {
    if (!userId) return []
    const params = search ? { search } : {}
    const response = await apiClient.get<UserNote[]>(
      `${SNA_PREFIX}users/${userId}/notes`,
      { params }
    )
    return response.data
  }

  return useQuery({
    queryFn: getUserNotes,
    queryKey: [...userNotesQueryKey(userId || ''), search],
    enabled: !!userId
  })
}

export function useCreateUserNote() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const onError = useOnError()
  const { t } = useTranslation('users')

  async function createNote({ userId, content }: CreateUserNoteParams) {
    const response = await apiClient.post<UserNote>(
      `${SNA_PREFIX}users/${userId}/notes`,
      { content }
    )
    return response.data
  }

  return useMutation({
    mutationFn: createNote,
    onSuccess: (_, variables) => {
      toast.success({ message: t('notes.created') })
      queryClient.invalidateQueries({ queryKey: userNotesQueryKey(variables.userId) })
    },
    onError
  })
}

export function useUpdateUserNote() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const onError = useOnError()
  const { t } = useTranslation('users')

  async function updateNote({ userId, noteId, content }: UpdateUserNoteParams) {
    const response = await apiClient.patch<UserNote>(
      `${SNA_PREFIX}users/${userId}/notes/${noteId}`,
      { content }
    )
    return response.data
  }

  return useMutation({
    mutationFn: updateNote,
    onSuccess: (_, variables) => {
      toast.success({ message: t('notes.updated') })
      queryClient.invalidateQueries({ queryKey: userNotesQueryKey(variables.userId) })
    },
    onError
  })
}

export function useDeleteUserNote() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const onError = useOnError()
  const { t } = useTranslation('users')

  async function deleteNote({ userId, noteId }: DeleteUserNoteParams) {
    await apiClient.delete(`${SNA_PREFIX}users/${userId}/notes/${noteId}`)
  }

  return useMutation({
    mutationFn: deleteNote,
    onSuccess: (_, variables) => {
      toast.success({ message: t('notes.deleted') })
      queryClient.invalidateQueries({ queryKey: userNotesQueryKey(variables.userId) })
    },
    onError
  })
}

export function useToggleContacted() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const onError = useOnError()
  const { t } = useTranslation('users')

  async function toggleContacted({ userId, wasContacted }: ToggleContactedParams) {
    const response = await apiClient.post(
      `${SNA_PREFIX}users/${userId}/toggle-contacted`,
      { was_contacted: wasContacted }
    )
    return response.data
  }

  return useMutation({
    mutationFn: toggleContacted,
    onSuccess: (_, variables) => {
      toast.success({ message: t('toast.contactedStatusUpdated') })
      queryClient.invalidateQueries({ queryKey: ['client', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
    onError
  })
}
